import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { HiOutlineUserGroup, HiUserAdd } from "react-icons/hi";
import UsersList from "../components/users/UsersList";
import PendingInvitations from "../components/users/PendingInvitations";
import InviteUserModal from "../components/users/InviteUserModal";
import Button from "../components/shared/Button";
import {
  fetchOrgUsersAction,
  fetchInvitationsAction,
} from "../redux/actions/usersActions";
import { useIsAdmin } from "../hooks/useIsAdmin";

const Users = () => {
  const dispatch = useDispatch();
  const currentOrganisation = useSelector(
    (state) => state.organisation?.currentOrganisation
  );
  const userData = useSelector((state) => state.auth?.userData);
  const { orgUsers, invitations } = useSelector((state) => state.users);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [activeTab, setActiveTab] = useState("users");

  const organisationId = currentOrganisation?._id;
  const isAdmin = useIsAdmin();

  useEffect(() => {
    if (organisationId) {
      dispatch(fetchOrgUsersAction(organisationId));
      if (isAdmin) {
        dispatch(fetchInvitationsAction(organisationId));
      }
    }
  }, [dispatch, organisationId, isAdmin]);

  return (
    <div className="p-4 md:p-8 min-h-screen">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-md bg-surface-soft text-ink border border-hairline">
              <HiOutlineUserGroup className="h-6 w-6" />
            </div>
            <h1 className="text-display-md text-ink" data-help-title="Users" data-help="Manage who from your team can access the dashboard. Invite colleagues by email, assign their role, and remove access when someone leaves. This page is admin-only.">Users</h1>
          </div>

          {isAdmin && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowInviteModal(true)}
              leadingIcon={<HiUserAdd className="h-4 w-4" />}
              data-help-title="Invite User"
              data-help="Opens a dialog to invite a new user to the dashboard by email and assign them a role of admin or viewer. LoyalBase emails them a unique invitation link. Admin only."
            >
              Invite User
            </Button>
          )}
        </div>

        {/* Tabs */}
        {isAdmin && (
          <div className="flex gap-1 mb-6 border-b border-hairline">
            <button
              onClick={() => setActiveTab("users")}
              className={`px-4 py-2 text-body-md font-medium border-b-2 transition-colors ${
                activeTab === "users"
                  ? "border-ink text-ink"
                  : "border-transparent text-muted hover:text-ink"
              }`}
              data-help-title="Members tab"
              data-help="Shows everyone who already belongs to your organisation, where you can change a user's role or remove them."
            >
              Members
            </button>
            <button
              onClick={() => setActiveTab("invitations")}
              className={`px-4 py-2 text-body-md font-medium border-b-2 transition-colors ${
                activeTab === "invitations"
                  ? "border-ink text-ink"
                  : "border-transparent text-muted hover:text-ink"
              }`}
              data-help-title="Invitations tab"
              data-help="Shows invitations that have been sent but not yet accepted, so you can track them, resend one, or revoke one."
            >
              Invitations
            </button>
          </div>
        )}

        {/* Content */}
        {activeTab === "users" && (
          <UsersList
            orgUsers={orgUsers}
            currentUserId={userData?._id}
            isAdmin={isAdmin}
            organisationId={organisationId}
          />
        )}

        {activeTab === "invitations" && isAdmin && (
          <PendingInvitations
            invitations={invitations}
            organisationId={organisationId}
          />
        )}
      </div>

      {isAdmin && (
        <InviteUserModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          organisationId={organisationId}
        />
      )}
    </div>
  );
};

export default Users;
